import React, { useState } from 'react';
import { motion } from 'framer-motion';
import FloatingBackground from '../../Common/FloatingBackground/FloatingBackground';
import './PlatformShowcase.css';
import { useContactModal } from '../../../stores/modalStore';

// Platform modules configuration
const platformModules = [
  {
    id: 'screen-templates',
    name: 'Screen Templates',
    description: 'A large variety of screen templates make it easy to create assessments.',
    mockups: ['cinquity-screen-template-1.png', 'cinquity-screen-template-2.png', 'cinquity-screen-template-3.png', 'cinquity-screen-template-4.png', 'cinquity-screen-template-5.png', 'cinquity-screen-template-6.png', 'cinquity-screen-template-7.png'],
    features: ['Visual Analogue Scale (VAS)', 'Joint Man', 'Multiple Choice Questions', 'Diverse eCOA Library'],
    ctaText: 'Explore our Wide Range of Screen Templates'
  },
  {
    id: 'cinquity-ai-studio',
    name: 'Cinquity AI Studio',
    description: 'Our AI-powered assessment creation tool allows fast development and delivery of a variety of assessments using natural language prompts',
    mockups: ['cinquity-ai-studio-1.png', 'cinquity-ai-studio-2.png', 'cinquity-ai-studio-3.png', 'cinquity-ai-studio-4.png'],
    features: ['AI-powered Assessment Creation Within Minutes', 'Assessment Edit/Review/Approval', 'Study Management'],
    ctaText: 'Try Cinquity AI Studio'
  },
  {
    id: 'localization',
    name: 'Localization',
    description: 'Localize your assessments into multiple languages within minutes to reach a global audience using our AI workflow. Or upload your own locales from a certified translation vendor.',
    mockups: ['cinquity-localization-1.png', 'cinquity-localization-2.png', 'cinquity-localization-3.png', 'cinquity-localization-4.png'],
    features: ['AI-powered Translation', '50+ Languages', 'Quick Validation Process'],
    ctaText: 'Start Localizing Your Assessments Today'
  },
  {
    id: 'smart-data-dashboard',
    name: 'Smart Data Dashboard',
    description: 'A comprehensive dashboard that provides a clear overview of the data collected from your assessments.',
    mockups: ['cinquity-smart-data-dashboard-1.png', 'cinquity-smart-data-dashboard-2.png', 'cinquity-smart-data-dashboard-3.png'],
    features: ['Electronic Data Capture (EDC)', 'Quick Data Export', 'Smart Study Data Insights'],
    ctaText: 'View Your Study Data Insights'
  },
  {
    id: 'byod',
    name: 'BYOD',
    description: 'Bring your own device (BYOD) support allows patients to complete assessments on their own devices, providing a more convenient and patient-friendly experience.',
    mockups: ['cinquity-byod-1.png','cinquity-byod-2.png'],
    features: ['Desktop/Tablet/Mobile', 'iOS/Android', 'Multi-browser Support'],
    ctaText: 'Learn More About Bring Your Own Device (BYOD)'
  }
];

const PlatformShowcase: React.FC = () => {
  const { open: openContactModal } = useContactModal();
  const [activeModule, setActiveModule] = useState('screen-templates');

  const currentModule = platformModules.find(m => m.id === activeModule);

  const handleModuleChange = (moduleId: string) => {
    setActiveModule(moduleId);
  };

  return (
    <motion.section 
      className="platform-showcase"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <FloatingBackground />
      <div className="container">
        {/* Header */}
        <div className="showcase-header">
          <h2 className="showcase-title">
            <span className="gradient-text">Integrated</span> Healthcare Platform
          </h2>
          <p className="showcase-subtitle">
            Seamless patient care and clinical management across every healthcare touchpoint
          </p>
        </div>

        {/* Module Tabs */}
        <div className="module-tabs">
          {platformModules.map((module) => (
            <button
              key={module.id}
              className={`tab-button ${activeModule === module.id ? 'active' : ''}`}
              onClick={() => handleModuleChange(module.id)}
            >
              <span className="tab-name">{module.name}</span>
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="showcase-content">
          {/* Mockup Display - Horizontal Scrollable Carousel */}
          <div className="mockup-section">
            <div className="mockup-carousel-container">
              <div 
                className="mockup-carousel"
              >
                {currentModule?.mockups.map((mockup, index) => (
                  <div key={`${activeModule}-${index}`} className="mockup-slide">
                    <img
                      src={`/Cinquity Website Mockups/${mockup}`}
                      alt={`${currentModule?.name} mockup ${index + 1}`}
                      className="module-mockup"
                    />
                  </div>
                ))}
              </div>
              
              {/* Scroll indicator */}
              <div className="scroll-indicator">
                <div className="scroll-hint">
                  <span className="scroll-text">Scroll to explore more</span>
                  <div className="scroll-arrows">
                    <span className="scroll-arrow">←</span>
                    <span className="scroll-arrow">→</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Highlights */}
          <div className="features-section">
            <div
              key={activeModule}
              className="feature-highlights"
            >
              <h3 className="module-title">{currentModule?.name}</h3>
              <p className="module-description">{currentModule?.description}</p>
              
              <div className="feature-list">
                {currentModule?.features.map((feature) => (
                  <div
                    key={feature}
                    className="feature-item"
                  >
                    <div className="feature-icon">✓</div>
                    <span className="feature-text">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="module-cta">
                <button 
                  className="explore-module-btn"
                  onClick={openContactModal}
                >
                  {currentModule?.ctaText}
                  <span className="btn-icon">→</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default PlatformShowcase;
